import React, { useEffect, useState } from "react";
import httpRequest from "../../../utils/httpRequest";
import API_URLS from "../../../utils/apiUrls";
import { FlashMessage } from "../../../components/common";
import { openUrlInNewTab } from "../../../utils/helper";
import appRoutes from "../../../routes/app.routes";
import Avatar from "../../../components/common/AvtarImage";
import _ from "lodash";


type Author = {
  id: number | string;
  slug?: string;
  name: string;
  profile_image?: string | null;
};

export type RelatedArticle = {
  id: string;
  slug: string;
  title: string;
  cover_image?: string | null;
  cover_video?: string | null; 
  created_at: string | number | Date;
  author: Author;
  content_preview?: string;      
  min_read?: number;           
};

type UrlBuilder = (slug: string) => string;

export interface RelatedArticlesSectionProps {
  postId: string;
  limit?: number;
  title?: string;
  buildArticleUrl?: UrlBuilder;
 onOpenArticle?: (slug: string, article: RelatedArticle) => void;

  ImageComponent?: React.ComponentType<{ src?: string | null; alt?: string; className?: string }>;
  AvatarComponent?: React.ComponentType<{ src?: string | null; className?: string }>;

  className?: string;
}

const fmtDate = (d: string | number | Date) =>
  new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const estimateRead = (title = "", preview = "") =>
  Math.max(
    1,
    Math.ceil(((title + " " + preview).trim().split(/\s+/).filter(Boolean).length + 220) / 200)
  );

const clampChars = (s = "", limit = 250) => {
  if (!s) return "";
  if (s.length <= limit) return s;
    const cut = s.slice(0, limit);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 160 ? cut.slice(0, lastSpace) : cut).trim() + "…";
};

const RelatedArticlesSection: React.FC<RelatedArticlesSectionProps> = ({
  postId,
  limit = 3,
  title = "Related articles",
  buildArticleUrl,
  onOpenArticle,
  ImageComponent,
  AvatarComponent,
  className,
}) => {
  const [list, setList] = useState<RelatedArticle[]>([]);
  const [loading, setLoading] = useState(false);

  const Img =
    ImageComponent ??
    (({ src, alt, className }: any) => <img src={src || ""} alt={alt || ""} className={className} />);

  const openArticle = (a: RelatedArticle) => {
     openUrlInNewTab(appRoutes.FeedDetails.generateFullPath(a?.slug));
  };

  const fetchRelated = async (id: string) => {
    try {
      setLoading(true);
      const res = await httpRequest().get(API_URLS.relatedArticles(id));
      if (res?.data?.status === 200) {
        const arr = Array.isArray(res.data.data) ? res.data.data : [];
        setList(arr);
      } else {
        setList([]);
        FlashMessage("Could not load related articles.", "error");
      }
    } catch (e) {
      console.log("related api error", e);
      setList([]);
      FlashMessage("Something went wrong while loading related articles.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (postId) fetchRelated(postId);
  }, [postId]);

  if (!loading && list.length === 0) return null;

  const displayItems: (RelatedArticle | null)[] = loading
    ? Array.from({ length: limit }, () => null)
    : list.slice(0, limit);

  return (
    <section className={`ra w-full mt-8 ${className || ""}`}>
      <h3 className="ra-title">{title}</h3>

      <div className="ra-grid">
        {displayItems.map((it, i) => (
          <article
            key={it ? it.id : `sk-${i}`}
            className="ra-card"
            onClick={() => it && openArticle(it)}
          >
            {/* Media */}
            <div className="ra-media">
              {it ? (
                !_.isEmpty(it.cover_image) ? (
                  <>
                    <Img src={it.cover_image} alt={it.title} className="ra-img" />
                    {it.cover_video ? (
                      <span className="ra-play">
                        <i className="fa fa-play" />
                      </span>
                    ) : null}
                  </>
                ) : (
                  <div className="ra-media-ph" />
                )
              ) : (
                <div className="ra-media-ph" />
              )}
            </div>

            {/* Body */}
            <div className="ra-body">
              {/* author row */}
              <div className="ra-author">
                {it ? (
                  <>
                    <Avatar src={it.author?.profile_image || ""} className="ra-avatar" />
                    <span className="ra-name">{it.author?.name}</span>
                    <span className="ra-dot">•</span>
                    <span>{fmtDate(it.created_at)}</span>
                  </>
                ) : (
                  <>
                    <span className="sk sk-avatar" />
                    <span className="sk sk-line w40" />
                  </>
                )}
              </div>

              {/* title */}
              <h4 className="ra-h4" title={it ? it.title : undefined}>
                {it ? it.title : <span className="sk sk-line" />}
              </h4>

              {/* content preview (250 chars max) */}
              {it ? (
                it.content_preview ? (
                  <p className="ra-excerpt">{clampChars(it.content_preview, 250)}</p>
                ) : null
              ) : (
                <p className="ra-excerpt">
                  <span className="sk sk-line w90" />
                  <span className="sk sk-line w80" />
                </p>
              )}

              {/* read time */}
              <div className="ra-read">
                {it
                  ? `${it.min_read ?? estimateRead(it.title, it.content_preview || "")} min read`
                  : <span className="sk sk-line w30" />}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default RelatedArticlesSection;

